import { dollars } from '@am/sim';

/*
 * The world score: one number that ranks every player on the server, however far they have got.
 * The server works it out from each player's records (see the relay's profiles.ts), which come
 * from the worlds in the saves they upload:
 *   - achievement points, as on the profile (they make the rank; at most MAX_POINTS),
 *   - the best airport's value, by orders of magnitude: VALUE_POINTS_PER_TENFOLD for every
 *     tenfold from VALUE_FLOOR ($1M 500, $10M 1000, $100M 1500, $1B 2000),
 *   - the best rating of an airport with real traffic, a point per rating point (five stars:
 *     1000).
 * Points and rating have a ceiling: once players have every achievement and a five-star airport,
 * those stop telling them apart. The value has none, so from there the more valuable airport ranks
 * higher, and players never tie for long.
 */

/** Value points for every tenfold of the best airport's value, counted from VALUE_FLOOR. */
export const VALUE_POINTS_PER_TENFOLD = 500;
export const VALUE_FLOOR = dollars(100_000);
/** The best rating there is (five stars). */
export const RATING_CEILING = 1000;

/** Points for an airport's value (cents). */
export function valuePoints(value: number): number {
  if (!(value > VALUE_FLOOR)) return 0;
  return Math.floor(VALUE_POINTS_PER_TENFOLD * Math.log10(value / VALUE_FLOOR));
}

export interface WorldScore {
  /** Achievement points. */
  readonly points: number;
  /** From the best airport's value. */
  readonly value: number;
  /** From the best rating. */
  readonly rating: number;
  readonly total: number;
}

/**
 * A player's world score from their achievement points, the value (cents) of their most valuable
 * airport and their best rating (0–1000) of an airport with enough traffic to count.
 */
export function worldScore(points: number, bestValue: number, bestRating: number): WorldScore {
  const value = valuePoints(bestValue);
  const rating = Math.max(0, Math.min(RATING_CEILING, Math.floor(bestRating)));
  return { points, value, rating, total: points + value + rating };
}
